import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { systemConfigService } from '../../services/systemConfigService';
import { SystemConfigFormData } from '../../types';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';
import Alert from '../../components/common/Alert';

const SystemConfigPage: React.FC = () => {
  const [formData, setFormData] = useState<SystemConfigFormData>({
    sistema_nome: '',
    sistema_email: '',
    sistema_telefone: '',
    sistema_endereco: '',
    mensalidade_valor: 0,
    taxa_adesao: 0,
    dias_vencimento: 10,
    notificacao_email: true,
    notificacao_sms: false,
    manutencao_modo: false,
    manutencao_mensagem: '',
    cobrar_adesao_cartao_hmg: false,
    logo: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await systemConfigService.get();
      setFormData({
        sistema_nome: data.sistema_nome || '',
        sistema_email: data.sistema_email || '',
        sistema_telefone: data.sistema_telefone || '',
        sistema_endereco: data.sistema_endereco || '',
        mensalidade_valor: data.mensalidade_valor || 0,
        taxa_adesao: data.taxa_adesao || 0,
        dias_vencimento: data.dias_vencimento || 10,
        notificacao_email: data.notificacao_email ?? true,
        notificacao_sms: data.notificacao_sms ?? false,
        manutencao_modo: data.manutencao_modo ?? false,
        manutencao_mensagem: data.manutencao_mensagem || '',
        cobrar_adesao_cartao_hmg: data.cobrar_adesao_cartao_hmg ?? false,
        logo: null,
      });
    } catch (err: any) {
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      const file = (e.target as HTMLInputElement).files?.[0] || null;
      setFormData((prev) => ({ ...prev, logo: file }));
    } else if (type === 'number') {
      setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setSaving(true);
      await systemConfigService.update(formData);
      setSuccess('Configurações salvas com sucesso!');
      loadConfig();
    } catch (err: any) {
      setError('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <Alert type="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Configurações do Sistema</h2>
        <p className="text-gray-600 mt-1">
          Configure informações gerais e parâmetros do sistema
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Informações Gerais
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Sistema
              </label>
              <Input
                type="text"
                name="sistema_nome"
                value={formData.sistema_nome}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email do Sistema
              </label>
              <Input
                type="email"
                name="sistema_email"
                value={formData.sistema_email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <Input
                type="text"
                name="sistema_telefone"
                value={formData.sistema_telefone}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endereço
              </label>
              <Input
                type="text"
                name="sistema_endereco"
                value={formData.sistema_endereco}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Valores e Taxas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor da Mensalidade (R$)
              </label>
              <Input
                type="number"
                step="0.01"
                name="mensalidade_valor"
                value={formData.mensalidade_valor}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taxa de Adesão (R$)
              </label>
              <Input
                type="number"
                step="0.01"
                name="taxa_adesao"
                value={formData.taxa_adesao}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dia de Vencimento
              </label>
              <Input
                type="number"
                name="dias_vencimento"
                value={formData.dias_vencimento}
                onChange={handleChange}
                min="1"
                max="28"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Notificações
          </h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notificacao_email"
                name="notificacao_email"
                checked={formData.notificacao_email}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="notificacao_email" className="ml-2 block text-sm text-gray-900">
                Enviar notificações por email
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notificacao_sms"
                name="notificacao_sms"
                checked={formData.notificacao_sms}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="notificacao_sms" className="ml-2 block text-sm text-gray-900">
                Enviar notificações por SMS
              </label>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Configurações do Cartão HMG
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cobrar Taxa de Adesão
              </label>
              <select
                name="cobrar_adesao_cartao_hmg"
                value={formData.cobrar_adesao_cartao_hmg ? 'true' : 'false'}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    cobrar_adesao_cartao_hmg: e.target.value === 'true',
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="false">Não cobrar taxa de adesão</option>
                <option value="true">Cobrar taxa de adesão</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {formData.cobrar_adesao_cartao_hmg
                  ? 'A primeira parcela será gerada na data de adesão com o valor da taxa de adesão.'
                  : 'Somente as mensalidades serão geradas conforme o dia de vencimento definido.'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Modo de Manutenção
          </h3>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="manutencao_modo"
                name="manutencao_modo"
                checked={formData.manutencao_modo}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="manutencao_modo" className="ml-2 block text-sm text-gray-900">
                Ativar modo de manutenção
              </label>
            </div>
            {formData.manutencao_modo && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem de Manutenção
                </label>
                <textarea
                  name="manutencao_mensagem"
                  value={formData.manutencao_mensagem}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mensagem que será exibida aos usuários durante a manutenção"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SystemConfigPage;
